import styled from "@emotion/styled";
import {SkpClass} from "api/quote";
import {GridContainer, GridItem} from "components/Grid";
import {useQuoteContext} from "pages/RateQuoteBind/contexts/quoteContext";
import {useCallback} from "react";
import Field from "../../Field";

const Note = styled.div`
  padding: 1%;
  margin-left: 5%;
  width: 100%;
  color: ${({theme}) => theme.color.state.orange};
`;

const numberOfFullTimeEmployees = [
  {
    label: 0,
    value: 0,
  },
  {
    label: 1,
    value: 1,
  },
  {
    label: 2,
    value: 2,
  },
  {
    label: 3,
    value: 3,
  },
  {
    label: 4,
    value: 4,
  },
  {
    label: 5,
    value: 5,
  },
  {
    label: 6,
    value: 6,
  },
  {
    label: 7,
    value: 7,
  },
];

const numberOfPartTimeEmployees = [
  {
    label: 0,
    value: 0,
  },
  {
    label: 1,
    value: 1,
  },
  {
    label: 2,
    value: 2,
  },
  {
    label: 3,
    value: 3,
  },
  {
    label: 4,
    value: 4,
  },
  {
    label: 5,
    value: 5,
  },
  {
    label: 6,
    value: 6,
  },
  {
    label: 7,
    value: 7,
  },
  {
    label: 8,
    value: 8,
  },
  {
    label: 9,
    value: 9,
  },
  {
    label: 10,
    value: 10,
  },
  {
    label: 11,
    value: 11,
  },
  {
    label: 12,
    value: 12,
  },
];

const classCodes = [
  {label: "Concrete Construction (91560)", value: "91560"},
  {
    label:
      "Appliances and Accessories - installation,servicing or repair - household (91155)",
    value: "91155",
  },
  {label: "Carpentry - NOC (91342)", value: "91342"},
  {
    label:
      "Carpet, Rug, Furniture or Upholstery Cleaning - on customers' premises  (91405)",
    value: "91405",
  },
  {
    label:
      "Contractors - subcontracted work - in connection with construction, reconstruction, erection or repair - not buildings (91581)",
    value: "91581",
  },
  {
    label: "Driveway, Parking Area or Sidewalk - paving or repaving (92215)",
    value: "92215",
  },
  {label: "Drywall or Wallboard Installation (92338)", value: "92338"},
  {label: "Electrical Work - within buildings  (92478)", value: "92478"},
  {label: "Fence Erection Contractors (94276)", value: "94276"},
  {
    label: "Floor Covering Installation - not ceramic tile or stone (94569)",
    value: "94569",
  },
  {
    label:
      "Heating or Combined Heating and Air Conditioning Systems or Equipment - dealers or distributors and installation, servicing or repair -no liquefied petroleum gas (LPG) equipment sales or work (95647)",
    value: "95647",
  },
  {
    label: "Heating or Combined w/AC – install/service/repair (95648)",
    value: "95648",
  },
  {label: "House Furnishings Installation (96053)", value: "96053"},
  {label: "Interior Decorators (96611)", value: "96611"},
  {label: "Janitorial Services (96816)", value: "96816"},
  {label: "Landscape Gardening (97047)", value: "97047"},
  {label: "Masonry (97447)", value: "97447"},
  {
    label:
      "Office Machines or Appliances - installation, inspection, adjustment or repair (98111)",
    value: "98111",
  },
  {
    label:
      "Painting - exterior - buildings or structures - three stories or less in height (98304)",
    value: "98304",
  },
  {label: "Paperhanging (98344)", value: "98344"},
  {label: "Plastering or Stucco Work (98449)", value: "98449"},
  {label: "Plumbing - residential or domestic (98483)", value: "98483"},
  {
    label:
      "Refrigeration Systems or Equipment - dealers and distributors and installation, servicing or repair - commercial(98636)",
    value: "98636",
  },
  {label: "Siding Installation (98967)", value: "98967"},
  {
    label:
      "Tile, Stone, Marble, Mosaic or Terrazzo Work - interior construction (99746)",
    value: "99746",
  },
];

const employeeFieldNames = [
  "current_full_time_employees",
  "current_part_time_employees",
];

function SkpClassForm({skp_class}: {skp_class: SkpClass}) {
  const {update} = useQuoteContext();

  const updateClass = useCallback(
    async (fieldNames: string[], newValue: string | number | null) => {
      for (let fieldName of fieldNames)
        await update("skp_class", ["skp"], skp_class.id, fieldName, newValue);
    },
    [skp_class.id, update]
  );

  return (
    <GridContainer rowGap={100} columnGap={16}>
      {!skp_class.primary_class && (
        <GridItem grow={12}>
          <Field.DropDown
            object={skp_class}
            label="Does this class have any employees?"
            field={"has_employees"}
            onChange={async (value) => {
              if (value === "true" && skp_class.has_employees !== true)
                await updateClass(employeeFieldNames, null);
            }}
            options={[
              {title: "Yes", value: true},
              {title: "No", value: false},
            ]}
          />
        </GridItem>
      )}
      <Field.DropDown
        object={skp_class}
        options={classCodes.map((item) => ({
          title: item.label,
          value: item.value,
        }))}
        disabled={
          !skp_class.primary_class &&
          !(
            skp_class.has_employees === true ||
            skp_class.has_employees === false
          )
        }
        field={"class_code"}
        label="Select Class"
        wrapperProps={{
          grow: 12,
        }}
      />

      {!skp_class.primary_class && skp_class.has_employees === false && (
        <Note>
          Note: Even though this class has no employees, a part time employee
          will be added and charged by default.
        </Note>
      )}

      <Field.DropDown
        object={skp_class}
        field={"current_full_time_employees"}
        label={"Number of full time employees"}
        disabled={
          !skp_class.primary_class &&
          (skp_class.has_employees === false ||
            !(
              skp_class.has_employees === true ||
              skp_class.has_employees === false
            ))
        }
        options={
          // if this is a secondary class
          !skp_class.primary_class
            ? // if user selected 0 part time employees, we remove the option to choose 0 full time employees
              skp_class.current_part_time_employees === 0
              ? numberOfFullTimeEmployees.slice(1).map((item) => ({
                  title: String(item.label),
                  value: item.value,
                }))
              : // otherwise, we include the option to choose 0 full time employees
                numberOfFullTimeEmployees.map((item) => ({
                  title: String(item.label),
                  value: item.value,
                }))
            : // if the class is a primary class, we never allow the user to choose 0 full time employees
              numberOfFullTimeEmployees.slice(1).map((item) => ({
                title: String(item.label),
                value: item.value,
              }))
        }
        wrapperProps={{
          grow: 12,
        }}
      />

      <Field.DropDown
        object={skp_class}
        field={"current_part_time_employees"}
        label={"Number of part time employees"}
        disabled={
          !skp_class.primary_class &&
          (skp_class.has_employees === false ||
            !(
              skp_class.has_employees === true ||
              skp_class.has_employees === false
            ))
        }
        options={
          // if user selected 0 full time employees, we remove the option to choose 0 part time employees
          skp_class.current_full_time_employees === 0
            ? numberOfPartTimeEmployees.slice(1).map((item) => ({
                title: String(item.label),
                value: item.value,
              }))
            : // otherwise, we let user choose 0 full time employees
              numberOfPartTimeEmployees.map((item) => ({
                title: String(item.label),
                value: item.value,
              }))
        }
        wrapperProps={{
          grow: 12,
        }}
      />
    </GridContainer>
  );
}

export default SkpClassForm;
